/**
 * ComponentModeField — visual picker for component automatic modes.
 *
 * Mirrors the HeroModeField two-section layout:
 *   - Active family panel at top (with variant tiles)
 *   - All families grid below (scrollable)
 *
 * Each tile renders an SVG preview via ComponentModePreview so users
 * can see at a glance what the layout looks like.
 *
 * Animation indicator
 * -------------------
 * Modes with ongoing motion get the same "▶ Animated" chip used by the
 * Hero picker (``HeroModeField``) — intentionally identical so the two
 * pickers read consistently. The old bespoke "motion" wave badge has
 * been retired in favour of this shared treatment.
 *
 * The canonical motion signal for a component mode is its
 * ``visual.dynamic`` flag (``carousel`` / ``shuffle`` / ``drift``),
 * serialised by ``ComponentModeDefinition.to_dict`` from the backend
 * definitions. ``modeHasMotion`` reads that flag (preferring an
 * explicit ``has_motion`` field if a future backend ships one) so the
 * indicator stays in sync with the renderer without a hand-maintained
 * id list.
 */
import React, { useMemo, useCallback } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { useMetadata } from "../../context/MetadataContext";
import {
  typography,
  surfaces,
  borders,
  textColors,
  interactive,
} from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import ComponentModePreview from "./ComponentModePreview";

// ---------------------------------------------------------------------------
// Variant count badge
// ---------------------------------------------------------------------------

const VariantCountBadge = React.memo(({ count, isSelected }) => (
  <span
    aria-label={`${count} variants`}
    className={[
      "inline-flex items-center gap-0.5 rounded-full",
      "px-1.5 py-0.5 text-[9px] font-semibold leading-none",
      "pointer-events-none",
      isSelected
        ? "bg-white/25 text-white"
        : `${surfaces.tertiary} ${textColors.tertiary} ${borders.default} border`,
    ].join(" ")}
  >
    +{count - 1}
  </span>
));
VariantCountBadge.displayName = "VariantCountBadge";

// ---------------------------------------------------------------------------
// Animation flag resolver
// ---------------------------------------------------------------------------
//
// Single source of truth for "does this mode merit the Animated chip?".
// Centralising the rule means every tile (active-family variants,
// all-families grid cards, initial-state grid, flat fallback) reads the
// same signal.
//
// Prefers an explicit ``has_motion`` field (forward-compatible with a
// future backend) and otherwise falls back to the ``visual.dynamic``
// flag — the canonical "this layout moves" marker on every animated
// component mode (carousel / shuffle / drift).

function modeHasMotion(mode) {
  if (!mode) return false;
  if (mode.has_motion != null) return Boolean(mode.has_motion);
  return Boolean(mode.visual && mode.visual.dynamic);
}

// ---------------------------------------------------------------------------
// Animated chip
// ---------------------------------------------------------------------------
//
// Identical to the Hero picker's chip so both pickers read the same:
// a small play triangle + "Animated" label pinned to the top-right of
// the preview frame. Two flavours — blue-tinted on unselected tiles,
// inverted white-on-blue on the selected tile.

const AnimatedChip = React.memo(({ isSelected, compact = false }) => (
  <span
    aria-label="Animated"
    className={[
      "absolute top-1 right-1 inline-flex items-center gap-0.5",
      "rounded-full pointer-events-none",
      "shadow-sm backdrop-blur-[2px]",
      compact ? "px-1 py-px" : "px-1.5 py-0.5",
      isSelected
        ? "bg-white/95 text-blue-700"
        : "bg-blue-500/90 text-white dark:bg-blue-400/90 dark:text-slate-900",
    ].join(" ")}
  >
    <svg
      viewBox="0 0 6 6"
      className={compact ? "w-1.5 h-1.5" : "w-2 h-2"}
      fill="currentColor"
      aria-hidden="true"
    >
      {/* Right-pointing play triangle */}
      <path d="M1.2 0.8 L1.2 5.2 L5 3 Z" />
    </svg>
    {!compact && (
      <span className="text-[8px] font-semibold tracking-wide leading-none uppercase">
        Animated
      </span>
    )}
  </span>
));
AnimatedChip.displayName = "AnimatedChip";

// ---------------------------------------------------------------------------
// Single tile
// ---------------------------------------------------------------------------

const ComponentModeTile = React.memo(function ComponentModeTile({
  modeId,
  label,
  description,
  isSelected,
  showAnimatedChip,
  variantCount,
  onClick,
  size = "md",
}) {
  const isMd = size === "md";
  return (
    <button
      type="button"
      onClick={onClick}
      title={description}
      aria-pressed={isSelected}
      className={[
        "group relative flex flex-col items-stretch text-left",
        "rounded-xl border transition-all overflow-hidden",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
        isMd ? "p-2" : "p-1.5",
        isSelected
          ? `${interactive.selectedBg} ${interactive.selectedBorder} shadow-md`
          : `${borders.default} ${surfaces.tertiary} ${surfaces.hover}`,
      ].join(" ")}
    >
      <div
        className={[
          "relative w-full rounded-md overflow-hidden",
          isMd ? "aspect-[3/2] mb-2" : "aspect-[3/2] mb-1.5",
          isSelected
            ? "bg-gradient-to-b from-blue-500/10 to-blue-500/30"
            : "bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900",
        ].join(" ")}
      >
        <ComponentModePreview modeId={modeId} isSelected={isSelected} />

        {showAnimatedChip && (
          <AnimatedChip isSelected={isSelected} compact={!isMd} />
        )}

        {isSelected && (
          <span
            className={[
              "absolute bottom-1 right-1 w-4 h-4 rounded-full shadow-sm",
              "bg-white/95 flex items-center justify-center",
            ].join(" ")}
          >
            <CheckOutlined style={{ fontSize: 8, color: "#2563eb" }} />
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-1 px-0.5 min-w-0">
        <span
          className={[
            "font-semibold truncate",
            isMd ? "text-[12px]" : "text-[11px]",
            isSelected ? "text-white" : textColors.primary,
          ].join(" ")}
        >
          {label}
        </span>
        {variantCount > 1 && (
          <VariantCountBadge count={variantCount} isSelected={isSelected} />
        )}
      </div>

      {description && isMd && (
        <span
          className={[
            "mt-0.5 px-0.5 text-[10px] leading-snug line-clamp-2",
            isSelected ? "text-white/70" : textColors.muted,
          ].join(" ")}
        >
          {description}
        </span>
      )}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Manual layout tile
// ---------------------------------------------------------------------------
//
// "Manual" is now the last tile in the picker's gallery rather than a
// separate Automatic/Manual toggle. It doesn't map to a
// ``component_mode``; selecting it flips the component into manual
// ResponsiveLayout territory (``manual_layout = true``) via
// ``onSelectManual``. The tile mirrors ``ComponentModeTile``'s shell but
// draws a bespoke "columns" glyph to signal "you arrange this yourself".

function ManualLayoutPreview() {
  return (
    <svg
      viewBox="0 0 60 40"
      className="w-full h-full"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect x="4" y="6" width="15" height="28" rx="2" className="fill-slate-400/70 dark:fill-slate-500/70" />
      <rect x="22.5" y="6" width="15" height="28" rx="2" className="fill-slate-400/70 dark:fill-slate-500/70" />
      <rect x="41" y="6" width="15" height="28" rx="2" className="fill-slate-400/70 dark:fill-slate-500/70" />
    </svg>
  );
}

const ManualLayoutTile = React.memo(function ManualLayoutTile({
  isSelected,
  onClick,
  size = "md",
}) {
  const isMd = size === "md";
  return (
    <button
      type="button"
      onClick={onClick}
      title="Manual — build a custom row layout"
      aria-pressed={isSelected}
      data-testid="component-mode-manual-tile"
      className={[
        "group relative flex flex-col items-stretch text-left",
        "rounded-xl border transition-all overflow-hidden",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
        isMd ? "p-2" : "p-1.5",
        isSelected
          ? `${interactive.selectedBg} ${interactive.selectedBorder} shadow-md`
          : `${borders.default} ${surfaces.tertiary} ${surfaces.hover}`,
      ].join(" ")}
    >
      <div
        className={[
          "relative w-full rounded-md overflow-hidden",
          isMd ? "aspect-[3/2] mb-2" : "aspect-[3/2] mb-1.5",
          isSelected
            ? "bg-gradient-to-b from-blue-500/10 to-blue-500/30"
            : "bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900",
        ].join(" ")}
      >
        <ManualLayoutPreview />

        {isSelected && (
          <span
            className={[
              "absolute bottom-1 right-1 w-4 h-4 rounded-full shadow-sm",
              "bg-white/95 flex items-center justify-center",
            ].join(" ")}
          >
            <CheckOutlined style={{ fontSize: 8, color: "#2563eb" }} />
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-1 px-0.5 min-w-0">
        <span
          className={[
            "font-semibold truncate",
            isMd ? "text-[12px]" : "text-[11px]",
            isSelected ? "text-white" : textColors.primary,
          ].join(" ")}
        >
          Manual
        </span>
      </div>

      {isMd && (
        <span
          className={[
            "mt-0.5 px-0.5 text-[10px] leading-snug line-clamp-2",
            isSelected ? "text-white/70" : textColors.muted,
          ].join(" ")}
        >
          Build a custom row layout you control per breakpoint.
        </span>
      )}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Active family panel (top section)
// ---------------------------------------------------------------------------

function ActiveFamilyPanel({ family, modesById, currentValue, onSelectVariant }) {
  const variants = useMemo(
    () => family.members.map((id) => modesById[id]).filter(Boolean),
    [family.members, modesById]
  );

  if (variants.length === 0) return null;

  return (
    <section
      className={[
        "rounded-xl border p-3",
        surfaces.tertiary,
        borders.default,
        "ring-1 ring-blue-300/60 dark:ring-blue-500/40",
      ].join(" ")}
      role="group"
      aria-label={`${family.label} variants`}
    >
      <div className="flex items-baseline gap-2 mb-2 px-0.5 min-w-0">
        <span
          className={[
            "text-[11px] font-bold uppercase tracking-wide whitespace-nowrap",
            textColors.tertiary,
          ].join(" ")}
        >
          Selected layout
        </span>
        <span className={`text-[12px] font-semibold whitespace-nowrap ${textColors.primary}`}>
          {family.label}
        </span>
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))" }}
      >
        {variants.map((mode) => {
          const isSelected = currentValue === mode.id;
          return (
            <ComponentModeTile
              key={mode.id}
              modeId={mode.id}
              label={mode.label}
              description={mode.description}
              isSelected={isSelected}
              showAnimatedChip={modeHasMotion(mode)}
              variantCount={1}
              onClick={() => onSelectVariant(mode.id)}
              size="sm"
            />
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// All families grid (bottom section)
// ---------------------------------------------------------------------------

function AllFamiliesGrid({
  families,
  modesById,
  activeFamily,
  currentValue,
  selectionActive = true,
  onSelectFamily,
  onSelectVariant,
  manualLayout = false,
  onSelectManual,
}) {
  if (families.length === 0) return null;

  return (
    <section className="flex flex-col" aria-label="All layouts">
      <div className="flex items-baseline justify-between mb-1 px-0.5">
        <span
          className={[
            "text-[11px] font-bold uppercase tracking-wide",
            textColors.tertiary,
          ].join(" ")}
        >
          All layouts
        </span>
        <span className={`text-[10px] ${textColors.muted}`}>
          {families.length} total
        </span>
      </div>

      {/* Scroll region. Sized taller than the old ``max-h-72`` so users
          can preview more layouts at once before reaching for the
          scrollbar; the active-family panel above stays pinned. */}
      <div className="max-h-[32rem] overflow-y-auto pr-1 rounded-lg">
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}
        >
          {families.map((family) => {
            const isActiveFamily = activeFamily != null && family.id === activeFamily.id;
            const previewModeId = isActiveFamily
              ? currentValue || family.default_member
              : family.default_member;

            // Family cards aggregate the "does it move?" signal: chip on
            // if the previewed mode OR any member is animated, so users
            // spot motion-bearing families without expanding them.
            const previewIsAnimated = modeHasMotion(modesById[previewModeId]);
            const anyMemberIsAnimated = family.members.some((id) =>
              modeHasMotion(modesById[id])
            );
            const showAnimatedChip = previewIsAnimated || anyMemberIsAnimated;

            const handleClick = isActiveFamily
              ? () => onSelectVariant(currentValue || family.default_member)
              : () => onSelectFamily(family);

            return (
              <ComponentModeTile
                key={family.id}
                modeId={previewModeId}
                label={family.label}
                description={family.description}
                // Highlight the featured family ONLY in automatic mode — in
                // Manual the sole "selected" tile is the Manual one.
                isSelected={isActiveFamily && selectionActive}
                showAnimatedChip={showAnimatedChip}
                variantCount={family.members.length}
                onClick={handleClick}
                size="md"
              />
            );
          })}
          {/* Manual layout always sits at the very end of the gallery. */}
          {typeof onSelectManual === "function" && (
            <ManualLayoutTile
              isSelected={!!manualLayout}
              onClick={onSelectManual}
              size="md"
            />
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Initial-state grid (no current value)
// ---------------------------------------------------------------------------

function InitialFamiliesGrid({
  families,
  modesById,
  onSelectFamily,
  manualLayout = false,
  onSelectManual,
}) {
  return (
    <div>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}
      >
        {families.map((family) => {
          const previewIsAnimated = modeHasMotion(modesById[family.default_member]);
          const anyMemberIsAnimated = family.members.some((id) =>
            modeHasMotion(modesById[id])
          );
          const showAnimatedChip = previewIsAnimated || anyMemberIsAnimated;
          return (
            <ComponentModeTile
              key={family.id}
              modeId={family.default_member}
              label={family.label}
              description={family.description}
              isSelected={false}
              showAnimatedChip={showAnimatedChip}
              variantCount={family.members.length}
              onClick={() => onSelectFamily(family)}
              size="md"
            />
          );
        })}
        {/* Manual layout always sits at the very end of the gallery. */}
        {typeof onSelectManual === "function" && (
          <ManualLayoutTile
            isSelected={!!manualLayout}
            onClick={onSelectManual}
            size="md"
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Family-grouped picker
// ---------------------------------------------------------------------------

function FamilyGroupedPicker({ modes, families, currentValue, onChange, manualLayout = false, onSelectManual }) {
  const modesById = useMemo(() => {
    const map = {};
    modes.forEach((m) => { map[m.id] = m; });
    return map;
  }, [modes]);

  // The featured family is derived from the (possibly dormant)
  // ``component_mode`` in BOTH layout modes, so the active-family panel +
  // "All layouts" grid stay visible even while Manual is selected. Only the
  // slot manager is hidden in Manual (gated in ``EditField``).
  const activeFamily = useMemo(() => {
    if (!currentValue) return null;
    return families.find((f) => f.members.includes(currentValue)) || null;
  }, [currentValue, families]);

  // Selection highlight: nothing automatic is highlighted while Manual owns
  // the selection (only the Manual tile is), but the featured family still
  // renders so the user can one-click back to an automatic layout.
  const selectedValue = manualLayout ? null : currentValue;
  const selectionActive = !manualLayout;

  const handleSelectVariant = useCallback(
    (modeId) => onChange(modeId),
    [onChange]
  );

  const handleSelectFamily = useCallback(
    (family) => onChange(family.default_member),
    [onChange]
  );

  if (!activeFamily) {
    return (
      <InitialFamiliesGrid
        families={families}
        modesById={modesById}
        onSelectFamily={handleSelectFamily}
        manualLayout={manualLayout}
        onSelectManual={onSelectManual}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ActiveFamilyPanel
        family={activeFamily}
        modesById={modesById}
        currentValue={selectedValue}
        onSelectVariant={handleSelectVariant}
      />
      <AllFamiliesGrid
        families={families}
        modesById={modesById}
        activeFamily={activeFamily}
        currentValue={currentValue}
        selectionActive={selectionActive}
        onSelectFamily={handleSelectFamily}
        onSelectVariant={handleSelectVariant}
        manualLayout={manualLayout}
        onSelectManual={onSelectManual}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flat picker fallback
// ---------------------------------------------------------------------------

function FlatPicker({ modes, currentValue, onChange, manualLayout = false, onSelectManual }) {
  const effectiveValue = manualLayout ? null : currentValue;
  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}
    >
      {modes.map((mode) => {
        const isSelected = effectiveValue === mode.id;
        return (
          <ComponentModeTile
            key={mode.id}
            modeId={mode.id}
            label={mode.label}
            description={mode.description}
            isSelected={isSelected}
            showAnimatedChip={modeHasMotion(mode)}
            variantCount={1}
            onClick={() => onChange(mode.id)}
            size="md"
          />
        );
      })}
      {/* Manual layout always sits at the very end of the gallery. */}
      {typeof onSelectManual === "function" && (
        <ManualLayoutTile
          isSelected={!!manualLayout}
          onClick={onSelectManual}
          size="md"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level field
// ---------------------------------------------------------------------------

export default function ComponentModeField({ label, help, value, onChange, infoText, manualLayout = false, onSelectManual }) {
  const { componentModes } = useMetadata();

  const modes = useMemo(() => componentModes?.modes || [], [componentModes]);
  const families = useMemo(() => componentModes?.families || [], [componentModes]);

  const currentValue = value || null;

  if (!modes.length) {
    return (
      <div className="field-wrapper">
        {label && (
          <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
            {label}
          </label>
        )}
        <div className={`text-center py-4 ${typography.caption} ${textColors.muted}`}>
          Loading component modes...
        </div>
      </div>
    );
  }

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? (
            <span className="ml-2">
              <InfoIcon text={infoText} />
            </span>
          ) : null}
        </label>
      )}

      {families.length > 0 ? (
        <FamilyGroupedPicker
          modes={modes}
          families={families}
          currentValue={currentValue}
          onChange={onChange}
          manualLayout={manualLayout}
          onSelectManual={onSelectManual}
        />
      ) : (
        <FlatPicker
          modes={modes}
          currentValue={currentValue}
          onChange={onChange}
          manualLayout={manualLayout}
          onSelectManual={onSelectManual}
        />
      )}

      {help && (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
    </div>
  );
}
